<template>
  <div
    ref="dropdownRef"
    class="dropdown"
    data-testid="bm-select"
    @focusout="handleFocusOut"
  >
    <label v-if="label" :id="`${id}-label`" :for="id" class="label">
      {{ label }}
    </label>

    <div
      :id="id"
      :class="['dropdown-toggle', variant, { open }]"
      :aria-expanded="open"
      :aria-controls="`${id}-listbox`"
      :aria-activedescendant="
        highlightedIndex > -1 ? getOptionId(highlightedIndex) : undefined
      "
      :aria-labelledby="label ? `${id}-label` : undefined"
      aria-haspopup="listbox"
      role="combobox"
      tabindex="0"
      @click="toggleDropdown"
      @keydown="handleKeyDown"
      data-testid="bm-select-toggle"
    >
      <input
        class="dropdown-input"
        :value="selectedLabel"
        :aria-label="ariaLabel"
        readonly
        tabindex="-1"
        aria-hidden="true"
      />
      <ChevronDownIcon
        aria-hidden="true"
        class="arrow"
        :aria-label="chevronIconAltText"
      />
    </div>

    <div
      v-if="open"
      :id="`${id}-listbox`"
      ref="listboxRef"
      class="dropdown-menu"
      :class="variant"
      role="listbox"
      tabindex="-1"
      data-testid="bm-select-menu"
    >
      <div
        v-for="(option, index) in options"
        :key="option.value"
        :id="getOptionId(index)"
        :class="[
          'dropdown-item',
          {
            selected: option.value === modelValue,
            highlighted: index === highlightedIndex
          }
        ]"
        role="option"
        :aria-selected="option.value === modelValue"
        @click="selectOption(option)"
        :data-testid="`bm-select-option-${option.value}`"
        @mouseenter="() => (highlightedIndex = index)"
        v-bind="option.attrs"
      >
        {{ option.label }}
      </div>
    </div>
  </div>
</template>

<script setup>
import '@/v2/design-system/styles/main.css';
import { computed, nextTick, ref, watch } from 'vue';
import ChevronDownIcon from '@/v2/design-system/assets/icons/ChevronDownIcon.vue';

const TYPE_AHEAD_TIMEOUT = 500;
const KEY_SPACE = ' ';

const props = defineProps({
  label: String,
  modelValue: String,
  options: { type: Array, required: true },
  variant: {
    type: String,
    default: 'blue',
    validator: v => ['light', 'dark', 'blue', 'outlined'].includes(v)
  },
  id: { type: String, default: 'bm-select' },
  chevronIconAltText: String,
  ariaLabel: String
});

const emit = defineEmits(['update:modelValue']);

const open = ref(false);
const highlightedIndex = ref(-1);
const searchBuffer = ref('');
const dropdownRef = ref(null);
const listboxRef = ref(null);
let searchTimeout = null;

const selectedLabel = computed(
  () => props.options.find(o => o.value === props.modelValue)?.label || ''
);

watch(open, async isOpen => {
  if (isOpen) {
    const index = props.options.findIndex(o => o.value === props.modelValue);
    highlightedIndex.value = index >= 0 ? index : 0;
  } else {
    searchBuffer.value = '';
  }
});

const toggleDropdown = () => {
  open.value = !open.value;
};

const closeDropdown = () => {
  open.value = false;
};

const handleFocusOut = e => {
  if (dropdownRef.value && !dropdownRef.value.contains(e.relatedTarget)) {
    closeDropdown();
  }
};

const selectOption = option => {
  if (!option || option.value === props.modelValue) {
    closeDropdown();
    return;
  }
  emit('update:modelValue', option.value);
  closeDropdown();
};

const getOptionId = index => `${props.id}-option-${index}`;

const handleKeyDown = event => {
  const { key } = event;

  switch (key) {
    case 'Enter':
    case KEY_SPACE:
      event.preventDefault();
      if (!open.value) {
        open.value = true;
      } else if (highlightedIndex.value >= 0) {
        selectOption(props.options[highlightedIndex.value]);
      }
      break;

    case 'ArrowDown':
    case 'ArrowUp':
      event.preventDefault();
      if (!open.value) {
        open.value = true;
      } else {
        navigateOptions(key === 'ArrowDown' ? 1 : -1);
      }
      break;

    case 'Escape':
    case 'Tab':
      closeDropdown();
      break;

    default:
      if (key.length === 1 && key.match(/\S/)) {
        if (!open.value) {
          open.value = true;
          nextTick(() => handleTypeAhead(key));
        } else {
          handleTypeAhead(key);
        }
      }
  }
};

const navigateOptions = step => {
  searchBuffer.value = '';

  const count = props.options.length;
  if (count === 0) return;

  const nextIndex = (highlightedIndex.value + step + count) % count;
  highlightedIndex.value = nextIndex;
  scrollToHighlight(nextIndex);
};

const scrollToHighlight = async index => {
  await nextTick();
  const element = listboxRef.value?.children[index];
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }
};

const handleTypeAhead = char => {
  clearTimeout(searchTimeout);

  searchBuffer.value += char.toLowerCase();

  const matchIndex = findBestMatchIndex(searchBuffer.value);
  if (matchIndex !== -1) {
    highlightedIndex.value = matchIndex;
    scrollToHighlight(matchIndex);
  }

  searchTimeout = setTimeout(() => {
    searchBuffer.value = '';
  }, TYPE_AHEAD_TIMEOUT);
};

const findBestMatchIndex = term => {
  let bestIndex = -1;
  let bestScore = 0;

  props.options.forEach((option, index) => {
    const text = option.label.toLowerCase();
    let score = 0;

    if (text === term)
      score = 100; // Exact match
    else if (text.startsWith(term))
      score = 80; // Starts with
    else if (text.includes(term))
      score = 60; // Contains
    else if (isSubsequence(term, text)) score = 40; // Fuzzy

    if (score > bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  });

  return bestIndex;
};

const isSubsequence = (term, text) => {
  let termIdx = 0;
  for (
    let textIdx = 0;
    textIdx < text.length && termIdx < term.length;
    textIdx++
  ) {
    if (text[textIdx] === term[termIdx]) termIdx++;
  }
  return termIdx === term.length;
};
</script>

<style scoped>
.dropdown {
  display: flex;
  flex-direction: column;
  position: relative;
}

.dropdown-toggle {
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--pad-space-sp-4);
}

.dropdown-toggle .arrow {
  color: var(--primary-text-icon-default);
  height: var(--icon-size-i-16);
  width: var(--icon-size-i-16);
}

.dropdown-toggle.light .dropdown-input,
.dropdown-toggle.light .arrow {
  color: var(--primary-text-icon-negative);
}

.dropdown-toggle.dark .dropdown-input,
.dropdown-toggle.dark .arrow {
  color: var(--neutral-text-icon-body);
}

.dropdown-toggle.outlined .dropdown-input {
  width: 100%;
  font-family: var(--body-small-reg-family), sans-serif;
  font-size: var(--body-base-reg-size);
  font-weight: var(--body-small-reg-weight);
  line-height: var(--line-height-lead-sm);
  color: var(--neutral-text-icon-body);
  padding: var(--pad-space-sp-12) var(--pad-space-sp-48) var(--pad-space-sp-12)
    var(--pad-space-sp-16);
  border-radius: var(--corner-radius-c-8);
  border: var(--border-width-b-1) solid var(--neutral-border-default);
  background-color: var(--neutral-surface-subtle);
  caret-color: var(--primary-border-default);
  outline: none;
  text-align: start;
  max-width: 100%;
}

.dropdown-toggle.outlined .dropdown-input:hover {
  border-color: var(--neutral-border-hover);
  background-color: var(--neutral-surface-default);
}

.dropdown-toggle.outlined.open .dropdown-input {
  border-color: var(--primary-border-default);
  outline: var(--border-width-b-1) solid var(--primary-surface-default);
}

.dropdown-toggle.outlined.open .arrow {
  color: var(--primary-border-default);
  rotate: 180deg;
}

.dropdown-toggle.outlined .arrow {
  position: absolute;
  right: var(--pad-space-sp-16);
  top: var(--pad-space-sp-15);
  cursor: pointer;
  height: var(--icon-size-i-16);
  width: var(--icon-size-i-16);
  color: var(--neutral-text-icon-pressed);
  transition: 0.2s linear;
}

.dropdown-input {
  font-family: var(--font-family-header), sans-serif;
  font-size: var(--body-base-med-size);
  font-weight: var(--body-base-med-weight);
  line-height: var(--line-height-lead-md);
  color: var(--primary-text-icon-default);
  background-color: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  text-align: end;
  max-width: 203px;
}

.label {
  color: var(--neutral-text-icon-label);
  font-family: var(--label-caption-reg-family), sans-serif;
  font-size: var(--body-small-reg-size);
  font-style: normal;
  font-weight: var(--label-caption-med-weight);
  line-height: var(--label-caption-med-line-height);
  margin-bottom: var(--pad-space-sp-8);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 223px;
  background-color: var(--neutral-surface-subtle);
  border: var(--border-width-b-1) solid var(--neutral-border-subtle);
  border-radius: var(--corner-radius-c-8);
  box-shadow: var(--outer-shadow-s-1-x) var(--outer-shadow-s-1-y)
    var(--outer-shadow-s-1-blur) 0 rgba(196, 196, 200, 0.24);
  max-height: 220px;
  padding: var(--pad-space-sp-4);
  z-index: 1000;
  overflow-y: scroll;
  scrollbar-color: var(--neutral-surface-darker) transparent;
  scrollbar-width: thin;
  scrollbar-gutter: stable;
  scroll-padding: 0;
}

.dropdown-menu.outlined {
  right: 0;
  left: 0;
}

.dropdown-item {
  padding: var(--pad-space-sp-6) var(--pad-space-sp-10);
  cursor: pointer;
  color: var(--neutral-text-icon-body);
  font-family: var(--body-base-reg-family), sans-serif;
  font-size: var(--body-base-reg-size);
  font-weight: var(--body-base-reg-weight);
  line-height: var(--line-height-lead-md);
  border-radius: var(--corner-radius-c-4);
}

.dropdown-item:not(:last-child) {
  margin-bottom: var(--pad-space-sp-4);
}

.dropdown-item.selected {
  color: black;
  font-weight: bold;
  background-color: var(--neutral-surface-disabled);
}

.dropdown-item:hover,
.dropdown-item.highlighted {
  background: var(--primary-surface-default);
  color: white;
}
</style>
